import * as React from "react";
import { Button, Drawer, Stack } from "@mui/material";
import { SelectOption } from "@mui/base";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { ClientConfig, ClientsService, WorkflowOption } from "gen/clients";
import { Form, Formik, FormikValues } from "formik";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { useMutation } from "react-query";
import { LoadingButton } from "../../../../../components/LoadingButton/LoadingButton";
import { ApiErrorMessage } from "../../../../../components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  clientConfig: ClientConfig;
  open: boolean;
  xtrfServices: SelectOption<number>[];
  onClose: () => void;
  onSuccess: (workflow: WorkflowOption) => void;
}

enum FieldNames {
  name = "name",
  description = "description",
  xtrfService = "xtrfService"
}

const CreateWorkflowPanel: React.FC<Props> = ({ clientConfig, open, xtrfServices, onSuccess, onClose }) => {
  const { mutateAsync, error } = useMutation({
    mutationFn: ClientsService.updateClientConfig,
    onSuccess
  });

  const onSubmit = React.useCallback(
    async formValues => {
      const addedWorkflow: WorkflowOption = {
        label: formValues[FieldNames.name],
        description: formValues[FieldNames.description] || undefined,
        xtrfServiceId: formValues[FieldNames.xtrfService]
      };
      await mutateAsync({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          selectableWorkflows: [...(clientConfig?.selectableWorkflows || []), addedWorkflow]
        }
      });

      if (addedWorkflow) {
        onSuccess(addedWorkflow);
      } else {
        onClose();
      }
    },
    [mutateAsync, clientConfig, onSuccess, onClose]
  );

  const validate = React.useCallback(
    (formValues: FormikValues) => {
      const errors: Record<string, string> = {};
      const name = formValues[FieldNames.name];
      if (name && clientConfig.selectableWorkflows?.some(w => w.label === name)) {
        errors[FieldNames.name] = "Workflow name must be unique";
      }
      return errors;
    },
    [clientConfig.selectableWorkflows]
  );

  return (
    <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 800 } }}>
      <DrawerHeader title="Add Workflow" onClose={onClose} />
      <DrawerBody>
        <Formik
          onSubmit={onSubmit}
          validate={validate}
          initialValues={{
            [FieldNames.name]: "",
            [FieldNames.description]: "",
            [FieldNames.xtrfService]: ""
          }}
        >
          {({ isSubmitting }) => (
            <Form autoComplete="off" autoCorrect="off" noValidate={true}>
              <Stack spacing={3}>
                {error && <ApiErrorMessage apiError={error} />}
                <TextInputField name={FieldNames.name} label="Workflow Name" required={true} />
                <TextInputField name={FieldNames.description} label="Description" />
                <SelectOneField
                  name={FieldNames.xtrfService}
                  options={xtrfServices}
                  label="XTRF Service"
                  required={true}
                />
              </Stack>
              <Stack spacing={1} direction="row" marginTop={4}>
                <LoadingButton isLoading={isSubmitting} color="primary" variant="contained" type="submit">
                  Add
                </LoadingButton>
                <Button color="secondary" onClick={onClose}>
                  Cancel
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </DrawerBody>
    </Drawer>
  );
};

export { CreateWorkflowPanel };
